import { Badge, Button, Input } from "@devhop/ui";
import { XIcon } from "lucide-react";
import { useState, useRef, KeyboardEvent } from "react";
import { slugify } from "@/shared/lib/slugify";

type Tag = {
  id: string;
  name: string;
  slug: string;
};

type TagInputProps = {
  value: Tag[];
  onChange: (tags: Tag[]) => void;
  placeholder?: string;
  disabled?: boolean;
};

export function TagInput({
  value = [],
  onChange,
  placeholder = "ພິມແລະກົດ Enter ເພື່ອເພີ່ມ tag",
  disabled,
}: TagInputProps) {
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault();
      const tagName = inputValue.trim();
      const slug = slugify(tagName);

      // Check if tag already exists
      if (value.find((t) => t.slug === slug)) {
        setInputValue("");
        return;
      }

      // Add new tag
      onChange([
        ...value,
        {
          id: slug, // Use slug as temporary ID
          name: tagName,
          slug,
        },
      ]);
      setInputValue("");
    } else if (e.key === "Backspace" && !inputValue && value.length > 0) {
      // Remove last tag on backspace when input is empty
      onChange(value.slice(0, -1));
    }
  };

  const removeTag = (slug: string) => {
    onChange(value.filter((t) => t.slug !== slug));
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2 rounded-md border bg-background p-2">
        {value.map((tag) => (
          <Badge
            key={tag.slug}
            variant="secondary"
            className="flex items-center gap-1"
          >
            {tag.name}
            {!disabled && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-4 w-4 p-0 hover:bg-transparent"
                onClick={() => removeTag(tag.slug)}
              >
                <XIcon className="h-3 w-3" />
              </Button>
            )}
          </Badge>
        ))}
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={value.length === 0 ? placeholder : ""}
          disabled={disabled}
          className="flex-1 border-0 p-0 shadow-none focus-visible:ring-0 min-w-[120px]"
        />
      </div>
      <p className="text-muted-foreground text-xs">
        ພິມຊື່ tag ແລ້ວກົດ Enter ເພື່ອເພີ່ມ (slug ຈະຖືກສ້າງອັດຕະໂນມັດ)
      </p>
    </div>
  );
}
